import { useState } from 'react'
import type { JobStatusResponse } from '../../types'
import { jobsApi } from '../../api/client'
import Button from '../ui/Button'
import Spinner from '../ui/Spinner'

interface JobProgressProps {
  status: JobStatusResponse
}

export default function JobProgress({ status }: JobProgressProps) {
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const getStatusColor = () => {
    switch (status.status) {
      case 'completed':
        return 'text-green-600'
      case 'failed':
        return 'text-red-600'
      case 'cancelled':
        return 'text-gray-600'
      default:
        return 'text-blue-600'
    }
  }

  const isRunning = status.status === 'pending' || status.status === 'processing'
  const hasUnresolved = (status.unresolved_count ?? 0) > 0

  // T-15: cancel the running job. The backend stops the worker at its next
  // cooperative checkpoint (never mid-operation) and the polling hook picks
  // up the resulting 'cancelled' status.
  const handleCancel = async () => {
    if (cancelling) return
    setCancelling(true)
    setCancelError(null)
    try {
      await jobsApi.cancel(status.id)
      setConfirmingCancel(false)
      // The polling hook will refresh the status to 'cancelled'.
    } catch (err) {
      setCancelError(
        err instanceof Error
          ? `No se pudo cancelar el trabajo: ${err.message}`
          : 'No se pudo cancelar el trabajo.'
      )
      setConfirmingCancel(false)
    } finally {
      setCancelling(false)
    }
  }

  return (
    <div className="space-y-4 p-6 bg-white border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Job Status</h3>
        <span className={`font-medium ${getStatusColor()}`}>
          {status.status.toUpperCase()}
        </span>
      </div>

      {status.status === 'processing' && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Spinner size="sm" />
            <span className="text-sm text-gray-600">
              {status.progress_message || 'Processing...'}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${status.progress}%` }}
            />
          </div>
          <div className="text-right text-sm text-gray-600">
            {status.progress}%
          </div>
        </div>
      )}

      {status.status === 'completed' && status.result_path && (
        <div className="text-green-600">
          ✓ PDF generated successfully!
        </div>
      )}

      {hasUnresolved && status.unresolved_cards && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
            <span>⚠️</span>
            <span>
              {status.unresolved_count} carta{status.unresolved_count !== 1 ? 's' : ''} no encontrada{status.unresolved_count !== 1 ? 's' : ''} en la base de datos
            </span>
          </div>
          <ul className="space-y-1">
            {status.unresolved_cards.map((card, idx) => (
              <li key={idx} className="text-sm text-red-600 flex items-start gap-2">
                <span className="text-red-400 mt-0.5">•</span>
                <span>
                  <span className="font-medium">{card.name}</span>
                  <span className="text-red-500 ml-1">({card.reason})</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {status.status === 'failed' && status.error && (
        <div className="text-red-600">
          ✗ Error: {status.error}
        </div>
      )}

      {status.status === 'cancelled' && (
        <div className="space-y-1">
          <div className="text-gray-600">
            ✋ Trabajo cancelado por el usuario.
          </div>
          <div className="text-sm text-gray-500">
            La generación se detuvo de forma segura: no se ha generado ningún PDF.
          </div>
        </div>
      )}

      {/* T-15: cancel button while the job is pending/processing, with an
          inline confirmation step before firing the request. */}
      {isRunning && (
        <div className="pt-2 border-t border-gray-100">
          {confirmingCancel ? (
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-gray-700">
                ¿Seguro que quieres cancelar la generación?
              </span>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleCancel}
                  disabled={cancelling}
                >
                  {cancelling ? (
                    <>
                      <Spinner size="sm" className="mr-2" />
                      Cancelando...
                    </>
                  ) : (
                    'Sí, cancelar'
                  )}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingCancel(false)}
                  disabled={cancelling}
                >
                  Seguir generando
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button
                variant="danger"
                size="sm"
                onClick={() => setConfirmingCancel(true)}
                disabled={cancelling}
              >
                Cancelar generación
              </Button>
            </div>
          )}
          {cancelError && (
            <p className="mt-2 text-sm text-red-600">{cancelError}</p>
          )}
        </div>
      )}
    </div>
  )
}
